/**
 * The cosmetic clinic: change what you were born with. A consultation with the surgeon (free), then the procedures
 * you choose, each priced on its own: a face contour, eyelids, a nose job, lip filler, body sculpting (in the clinic:
 * the gym is the other way to a body). A few hours under, a couple of days' swelling, and it's you from then on.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const CLINICS: Record<string, string> = {
  lagos: 'Glow Aesthetic Clinic, Ikoyi', us: 'Sculpt Aesthetics, Beverly Hills', france: 'Clinique Lumière Esthétique, Paris 16e',
  china: 'Yanzhi Aesthetic Hospital, Jing’an', japan: 'Hikari Beauty Clinic, Ginza', italy: 'Clinica Estetica Bellavista, Milan', uae: 'Al Noor Cosmetic Surgery, Jumeirah',
};
export const clinicFor = (region: string): string => CLINICS[region] ?? CLINICS.lagos;

export type Procedure = 'face' | 'eyes' | 'nose' | 'lips' | 'body';
export const PROCEDURES: Record<Procedure, { label: string; price: number; hours: number; blurb: string }> = {
  face: { label: 'Face contouring', price: 3_800_000, hours: 4, blurb: 'Jaw, chin and cheekbones reshaped.' },
  eyes: { label: 'Eyelid surgery', price: 2_200_000, hours: 2, blurb: 'The shape and set of the eyes.' },
  nose: { label: 'Rhinoplasty', price: 2_900_000, hours: 3, blurb: 'A new nose: narrower, straighter, smaller, or just different.' },
  lips: { label: 'Lip filler', price: 450_000, hours: 1, blurb: 'Fuller, thinner, a sharper cupid’s bow. Half an hour in the chair.' },
  body: { label: 'Body sculpting', price: 5_200_000, hours: 5, blurb: 'Liposuction, a sculpted waist, the bust or chest you want.' },
};
export const procedurePrice = (s: LifeState, p: Procedure) => localPrice(s, PROCEDURES[p].price, 10_000);

interface CosmeticState { done?: Procedure[]; healing?: number }
const of = (s: LifeState): CosmeticState => ((s as LifeState & { cosmetic?: CosmeticState }).cosmetic ??= {});
/** still swollen (the two days after) */
export const healing = (s: LifeState) => (of(s).healing ?? 0) > s.minutes;

export function haveProcedures(s: LifeState, list: Procedure[]): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!list.length) return { ok: false, line: '', events: [] };
  const cost = list.reduce((a, p) => a + procedurePrice(s, p), 0);
  if (!spend(s, cost, `${list.map((p) => PROCEDURES[p].label).join(', ')} (the clinic)`)) return { ok: false, line: 'Your card is declined. The receptionist is very kind about it.', events: [] };
  const st = of(s), hours = Math.min(8, list.reduce((a, p) => a + PROCEDURES[p].hours, 0));
  st.done = [...new Set([...(st.done ?? []), ...list])];
  const ev = advance(s, hours * 60, { busy: true });
  const big = list.some((p) => p !== 'lips');
  if (big) st.healing = s.minutes + 2 * 1440;
  s.needs.energy = Math.max(5, s.needs.energy - (big ? 35 : 5));
  s.history.push({ t: s.minutes, text: `Had ${list.map((p) => PROCEDURES[p].label.toLowerCase()).join(' and ')}.` });
  return { ok: true, line: big ? 'You wake up in recovery, bandaged and groggy. The surgeon is pleased. Two days of swelling, then the mirror.' : 'Thirty minutes, a sting, an ice pack. In the mirror: yes.', events: ev };
}
